/* eslint-disable @tencent/i18next/no-literal-string */
import _set from 'lodash/set';
import type { DeepPartial } from '@src/slide/infrastructure/module/independent/ts-tools/deep-partial';
import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import {
    BaseChartPresetHandler,
    ModifyPlotAreaProps,
    SeriesInitialProps,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { BarChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/bar-chart-series';
import { BarChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/bar-chart';
import {
    IChartAxis,
    IChartPlotArea,
    IChartSpace,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { IBaseSubChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/base-sub-chart.interface';
import {
    STAxPos,
    STBarDir,
    STBarGrouping,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { IBarChart } from '../../../Interface/chart-v3/sub-chart';

export class BarChartPresetHandler extends BaseChartPresetHandler {
    get ChartSeries() {
        return BarChartSeries;
    }
    get SubChart() {
        return BarChart;
    }

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): IBarChart {
        const subChart = super.createChartModel(type, chartSpace, initialProps);

        switch (type) {
            case PresetChartType.COLUMN_CLUSTERED:
                this.setColumnClustered(subChart);
                break;
            case PresetChartType.COLUMN_STACKED:
                this.setColumnStacked(subChart);
                break;
            case PresetChartType.COLUMN_PERCENT_STACKED:
                this.setColumnPercentStacked(subChart);
                break;
            case PresetChartType.BAR_CLUSTERED:
                this.setBarClustered(subChart);
                break;
            case PresetChartType.BAR_STACKED:
                this.setBarStacked(subChart);
                break;
            case PresetChartType.BAR_PERCENT_STACKED:
                this.setBarPercentStacked(subChart);
                break;
        }

        return subChart;
    }

    public modifyPlotArea(param: {
        plotArea: IChartPlotArea;
        newSubChart: IBaseSubChart;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
    }): ModifyPlotAreaProps {
        const { plotArea, targetType } = param;
        const { updateProps = {}, removeProps } = super.modifyPlotArea(param);
        const hasCategoryAxes = !!plotArea.categoryAxes;
        const hasValueAxes = !!plotArea.valueAxes;
        const hasDataAxes = !!plotArea.dateAxes;

        if (hasCategoryAxes) {
            updateProps.categoryAxes = [
                {
                    ...updateProps?.categoryAxes?.[0],
                    position: this.getCategoryAxisPosition(targetType),
                },
                ...(updateProps?.categoryAxes?.slice(1) || []),
            ];
        }
        if (hasValueAxes) {
            updateProps.valueAxes = [
                {
                    ...updateProps?.valueAxes?.[0],
                    position: this.getValueAxisPosition(targetType),
                },
                ...(updateProps?.valueAxes?.slice(1) || []),
            ];
        }
        if (hasDataAxes) {
            updateProps.dateAxes = [
                {
                    ...updateProps?.dateAxes?.[0],
                    // 和category位置一样
                    position: this.getCategoryAxisPosition(targetType),
                },
                ...(updateProps?.dateAxes?.slice(1) || []),
            ];
        }

        return {
            updateProps,
            removeProps,
        };
    }

    protected shouldExtendTrendline(targetType: PresetChartType) {
        return targetType === PresetChartType.BAR_CLUSTERED || targetType === PresetChartType.COLUMN_CLUSTERED;
    }

    protected createCategoryAxis(props: Partial<IChartAxis>, type: PresetChartType): IChartAxis {
        const baseAxis = super.createCategoryAxis(props, type);
        if (this.isBarChart(type)) {
            return {
                ...baseAxis,
                position: STAxPos.l,
            };
        }

        return baseAxis;
    }

    protected createValueAxis(props: Partial<IChartAxis>, type: PresetChartType, grouping?: STBarGrouping): IChartAxis {
        const baseAxis = super.createValueAxis(props, type, grouping);
        if (this.isBarChart(type)) {
            return {
                ...baseAxis,
                position: STAxPos.b,
            };
        }

        return baseAxis;
    }

    protected createDateAxis(props: Partial<IChartAxis>, type: PresetChartType, grouping?: STBarGrouping): IChartAxis {
        const baseAxis = super.createDateAxis(props, type);
        if (this.isBarChart(type)) {
            return {
                ...baseAxis,
                position: STAxPos.l,
            };
        }

        return baseAxis;
    }

    protected getCategoryAxisPosition(targetType) {
        return this.isBarChart(targetType) ? STAxPos.l : STAxPos.b;
    }
    protected getValueAxisPosition(targetType) {
        return this.isBarChart(targetType) ? STAxPos.b : STAxPos.l;
    }

    protected handleAxisPropsOnModify(param: {
        subChart: IBaseSubChart;
        plotArea: IChartPlotArea;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
        updateProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
        removeProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
    }) {
        const { targetType, updateProps, plotArea, subChart } = param;

        const isTargetBarChart = this.isBarChart(targetType);
        const isCurrentBarChart = this.isBarChartByModel(subChart as BarChart);
        // 如果切换类型是条形图，或者 当前不是条形图就不处理
        if (isTargetBarChart || !isCurrentBarChart) {
            return;
        }

        const { categoryAxes, valueAxes, dateAxes } = plotArea;
        const valueAxis = valueAxes?.[0];
        const dateAxis = dateAxes?.[0];
        const categoryAxis = categoryAxes?.[0];

        // 条形图比较特殊，在其他图表转换成条形图的时候，会将坐标轴的位置调换
        // 所以在切成其他的图表时，如果有坐标轴，就把值坐标轴移到他们默认的位置
        if (valueAxis && valueAxis.position !== this.getValueAxisPosition(targetType)) {
            _set(updateProps, 'valueAxes[0].position', this.getValueAxisPosition(targetType));
        }
        if (categoryAxis && categoryAxis.position !== this.getCategoryAxisPosition(targetType)) {
            _set(updateProps, 'categoryAxes[0].position', this.getCategoryAxisPosition(targetType));
        }
        if (dateAxis && dateAxis.position !== this.getDateAxisPosition(targetType)) {
            _set(updateProps, 'dateAxes[0].position', this.getDateAxisPosition(targetType));
        }
    }

    // 以下都拆成对于"图表类型"应用相关的一些接口，便于在"创建"和"修改"图表都能够一定程度上复用
    // 设置为"簇状柱形图"
    private setColumnClustered(subChart: IBarChart) {
        subChart.direction = STBarDir.col;
        this.setClustered(subChart, 'col');
    }
    // 设置为"堆积柱形图"
    private setColumnStacked(subChart: IBarChart) {
        subChart.direction = STBarDir.col;
        this.setStacked(subChart);
    }
    // 设置为"百分比堆积柱形图"
    private setColumnPercentStacked(subChart: IBarChart) {
        subChart.direction = STBarDir.col;
        this.setStacked(subChart, true);
    }
    // 设置为"簇状柱形图"
    private setBarClustered(subChart: IBarChart) {
        subChart.direction = STBarDir.bar;
        this.setClustered(subChart, 'bar');
    }
    // 设置为"堆积柱形图"
    private setBarStacked(subChart: IBarChart) {
        subChart.direction = STBarDir.bar;
        this.setStacked(subChart);
    }
    // 设置为"百分比堆积柱形图"
    private setBarPercentStacked(subChart: IBarChart) {
        subChart.direction = STBarDir.bar;
        this.setStacked(subChart, true);
    }

    private setClustered(subChart: IBarChart, type: 'col' | 'bar') {
        subChart.grouping = STBarGrouping.clustered;

        if (type === 'col') {
            subChart.overlap = -27;
            subChart.gapWidth = 219;
        } else {
            subChart.overlap = 0;
            subChart.gapWidth = 182;
        }
    }
    private setStacked(subChart: IBarChart, isPercent?: boolean) {
        subChart.grouping = isPercent ? STBarGrouping.percentStacked : STBarGrouping.stacked;
        subChart.overlap = 100;
        subChart.gapWidth = 150;
    }

    private isBarChart(type: PresetChartType) {
        return (
            type === PresetChartType.BAR_CLUSTERED
            || type === PresetChartType.BAR_PERCENT_STACKED
            || type === PresetChartType.BAR_STACKED
        );
    }

    private isBarChartByModel(subChart: IBarChart) {
        return subChart.direction === STBarDir.bar;
    }
}
